import React from 'react';
import { TrendingDown, TrendingUp, RefreshCw } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { fetchLiquidations, LiquidationData } from '../lib/coinglass';

interface LiquidationsProps {
  symbol?: string;
}

interface ViewLiquidationData {
  longLiquidation: number;
  shortLiquidation: number;
  totalLiquidation: number;
  time: number;
}

const Liquidations: React.FC<LiquidationsProps> = ({ symbol = 'BTC' }) => {  const { data, error, isLoading, refetch } = useQuery<ViewLiquidationData>({
    queryKey: ['liquidations', symbol],
    queryFn: async () => {      const result = await fetchLiquidations();
      const symbolLiquidations = result.filter((item: LiquidationData) => item.symbol.toUpperCase() === symbol.toUpperCase());
      
      if (!symbolLiquidations.length) {
        throw new Error('No liquidation data available for ' + symbol);
      }      // Calculate total liquidations for each side based on API response
      const longLiquidations = symbolLiquidations
        .reduce((sum: number, item: LiquidationData) => sum + item.long_liquidation_usd_24h, 0);
      
      const shortLiquidations = symbolLiquidations
        .reduce((sum: number, item: LiquidationData) => sum + item.short_liquidation_usd_24h, 0);

      return {
        longLiquidation: longLiquidations,
        shortLiquidation: shortLiquidations,
        totalLiquidation: longLiquidations + shortLiquidations,
        time: Date.now()
      };
    },
    refetchInterval: 5 * 60 * 1000, // 5 minutes
  });

  const formatUSD = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      notation: 'compact',
      maximumFractionDigits: 2
    }).format(value);
  };

  if (error) return null;
  if (isLoading || !data) return null;

  return (
    <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Liquidations (24h)</h3>
        <button
          onClick={() => refetch()}
          className="p-2 text-gray-400 hover:text-yellow-400 transition-colors"
          aria-label="Refresh"
        >
          <RefreshCw size={20} />
        </button>
      </div>
      
      <div className="space-y-6">
        <div className="space-y-2">
          <div className="text-sm text-gray-400">Total Liquidations</div>
          <div className="text-3xl font-bold text-white">
            {formatUSD(data.totalLiquidation)}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <TrendingDown className="text-red-500" />
              <span className="text-sm text-gray-400">Long Liquidations</span>
            </div>
            <div className="text-xl font-bold text-white">
              {formatUSD(data.longLiquidation)}
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <TrendingUp className="text-green-500" />
              <span className="text-sm text-gray-400">Short Liquidations</span>
            </div>
            <div className="text-xl font-bold text-white">
              {formatUSD(data.shortLiquidation)}
            </div>
          </div>
        </div>

        <div className="text-xs text-gray-400">
          Last updated: {new Date(data.time).toLocaleString()}
        </div>
      </div>
    </div>
  );
};

export default Liquidations;